// src/templateManager/ContextFieldsEditor.jsx
// Flexible editor for context field categories and fields

import { useState } from 'react';
import {
  Stack,
  Tabs,
  Button,
  Card,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Modal,
  Alert,
  Divider,
  Badge
} from '@mantine/core';
import { IconPlus, IconTrash, IconAlertCircle, IconEye } from '@tabler/icons-react';
import FieldEditor from './FieldEditor';

function toCategoryLabel(str) {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b(Profile|Fields)\b/gi, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
}

export default function ContextFieldsEditor({ value = {}, onChange }) {
  const [activeCategory, setActiveCategory] = useState(
    Object.keys(value || {})[0] || null
  );
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [previewMode, setPreviewMode] = useState(false);

  const categories = Object.keys(value || {});

  // Add new category
  const handleAddCategory = () => {
    if (!newCategoryName.trim()) return;

    const categoryKey = newCategoryName
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[^a-z0-9]/g, '');

    if (value[categoryKey]) {
      alert('Category already exists!');
      return;
    }

    onChange({
      ...value,
      [categoryKey]: []
    });

    setActiveCategory(categoryKey);
    setNewCategoryName('');
    setAddCategoryModalOpen(false);
  };

  // Delete category
  const handleDeleteCategory = (categoryKey) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete category "${toCategoryLabel(categoryKey)}"? This cannot be undone.`)) {
      return;
    }

    const newValue = { ...value };
    delete newValue[categoryKey];
    onChange(newValue);

    // Set active to first remaining category
    const remaining = Object.keys(newValue);
    setActiveCategory(remaining[0] || null);
  };

  // Add new field to category
  const handleAddField = () => {
    if (!activeCategory) return;

    const newField = {
      name: '',
      type: 'text',
      description: '',
      required: false
    };

    onChange({
      ...value,
      [activeCategory]: [...(value[activeCategory] || []), newField]
    });
  };

  // Update field
  const handleUpdateField = (index, updatedField) => {
    const fields = [...(value[activeCategory] || [])];
    fields[index] = updatedField;

    onChange({
      ...value,
      [activeCategory]: fields
    });
  };

  // Delete field
  const handleDeleteField = (index) => {
    const fields = [...(value[activeCategory] || [])];
    fields.splice(index, 1);

    onChange({
      ...value,
      [activeCategory]: fields
    });
  };

  // Move field up/down
  const handleMoveField = (index, direction) => {
    const fields = [...(value[activeCategory] || [])];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= fields.length) return;

    [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];

    onChange({
      ...value,
      [activeCategory]: fields
    });
  };

  const currentFields = activeCategory ? (value[activeCategory] || []) : [];

  return (
    <Stack spacing="md">
      {/* Header */}
      <Group position="apart">
        <div>
          <Text size="lg" weight={600}>Context Configuration</Text>
          <Text size="sm" color="dimmed">
            Define context forms and fields for this template
          </Text>
        </div>
        <Group>
          <Button
            variant="light"
            leftIcon={<IconEye size={16} />}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? 'Edit Mode' : 'Preview'}
          </Button>
        </Group>
      </Group>

      <Alert icon={<IconAlertCircle />} color="blue">
        <Text size="sm">
          Each <strong>category</strong> becomes a form section. <strong>Fields</strong> are the inputs within each section (text, dropdown, multiselect, etc.)
        </Text>
      </Alert>

      {/* Category Tabs */}
      <Card withBorder p="md">
        <Stack>
          <Group position="apart">
            <Text weight={500}>Categories</Text>
            <Button
              size="xs"
              leftIcon={<IconPlus size={14} />}
              onClick={() => setAddCategoryModalOpen(true)}
            >
              Add Category
            </Button>
          </Group>

          <Tabs value={activeCategory} onTabChange={setActiveCategory}>
            <Tabs.List>
              {categories.map((cat) => (
                <Tabs.Tab key={cat} value={cat}>
                  <Group spacing="xs">
                    {toCategoryLabel(cat)}
                    <Badge size="xs">{(value[cat] || []).length}</Badge>
                    {categories.length > 1 && (
                      <ActionIcon
                        size="xs"
                        color="red"
                        variant="subtle"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCategory(cat);
                        }}
                      >
                        <IconTrash size={12} />
                      </ActionIcon>
                    )}
                  </Group>
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>

          {categories.length === 0 && (
            <Alert color="yellow">
              <Text size="sm">No categories yet. Click "Add Category" to get started.</Text>
            </Alert>
          )}
        </Stack>
      </Card>

      {/* Fields Editor */}
      {activeCategory && !previewMode && (
        <Card withBorder p="md">
          <Stack>
            <Group position="apart">
              <Text weight={500}>
                {toCategoryLabel(activeCategory)} Form
              </Text>
              <Button
                size="sm"
                leftIcon={<IconPlus size={14} />}
                onClick={handleAddField}
              >
                Add Field
              </Button>
            </Group>

            <Divider />

            {currentFields.length > 0 ? (
              <Stack spacing="md">
                {currentFields.map((field, idx) => (
                  <FieldEditor
                    key={idx}
                    field={field}
                    onUpdate={(updated) => handleUpdateField(idx, updated)}
                    onDelete={() => handleDeleteField(idx)}
                    onMoveUp={() => handleMoveField(idx, 'up')}
                    onMoveDown={() => handleMoveField(idx, 'down')}
                    canMoveUp={idx > 0}
                    canMoveDown={idx < currentFields.length - 1}
                  />
                ))}
              </Stack>
            ) : (
              <Alert color="gray">
                <Text size="sm">No fields yet. Click "Add Field" to create one.</Text>
              </Alert>
            )}
          </Stack>
        </Card>
      )}

      {/* Preview Mode */}
      {activeCategory && previewMode && (
        <Card withBorder p="md">
          <Stack>
            <Text weight={500}>{toCategoryLabel(activeCategory)} Preview</Text>
            <Divider />
            <Stack spacing="sm">
              {currentFields.map((field, idx) => (
                <div key={idx}>
                  <Text size="sm" weight={500}>
                    {toCategoryLabel(field.name)}
                    {field.required && <Text component="span" color="red"> *</Text>}
                  </Text>
                  <Text size="xs" color="dimmed">{field.description}</Text>
                  <Text size="xs" color="blue">Type: {field.type}</Text>
                  {field.options && (
                    <Text size="xs" color="dimmed">
                      Options: {field.options.join(', ')}
                    </Text>
                  )}
                </div>
              ))}
            </Stack>
          </Stack>
        </Card>
      )}

      {/* Add Category Modal */}
      <Modal
        opened={addCategoryModalOpen}
        onClose={() => {
          setAddCategoryModalOpen(false);
          setNewCategoryName('');
        }}
        title="Add Context Category"
      >
        <Stack>
          <TextInput
            label="Category Name"
            placeholder="e.g., Healthcare Regulations, Technical Context"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
          />
          <Text size="xs" color="dimmed">
            Category key will be: {newCategoryName.toLowerCase().replace(/\s+/g, '')}
          </Text>
          <Group position="right">
            <Button variant="subtle" onClick={() => setAddCategoryModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCategory}>Add Category</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
